import { DsBadge, DsButton } from "../../../design-system";

function initialsFromName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

function resolveLabel(id, list, fallback) {
  if (!id || !Array.isArray(list)) return fallback || "";
  const row = list.find((x) => String(x.id) === String(id));
  return row?.name || fallback || "";
}

/**
 * Profile completion hero — presentational only.
 * Completion and incomplete sections come from existing validators in the parent.
 * "Complete my profile" CTA is ESS-only (showCompleteCta); admin/onboard hide it.
 */
export function ProfileHero({
  employeeData = {},
  masterData = {},
  completionPercent = 0,
  incompleteSections = [],
  onJumpToSection,
  onCompleteProfile,
  forceReadOnly = false,
  showCompleteCta = false,
}) {
  const name =
    employeeData?.user?.name ||
    employeeData?.name ||
    employeeData?.candidateName ||
    "Employee";
  const empCode = employeeData?.empCode || employeeData?.candidateId || "";
  const status = employeeData?.employmentStatus || "";
  const type = employeeData?.employmentType || employeeData?.employeeType || "";

  const designation =
    employeeData?.designation?.name ||
    resolveLabel(employeeData?.designationId, masterData.designations, "");
  const department =
    employeeData?.department?.name ||
    resolveLabel(employeeData?.departmentId, masterData.departments, "");
  const branch =
    employeeData?.branch?.name ||
    resolveLabel(employeeData?.branchId, masterData.branches, "");

  const sub = [designation, department, branch].filter(Boolean).join(" · ");
  const pct = Math.max(0, Math.min(100, Math.round(completionPercent || 0)));
  const r = 34;
  const c = 2 * Math.PI * r;
  const dash = (c * pct) / 100;
  const missing = incompleteSections.slice(0, 4);
  const extra = incompleteSections.length - missing.length;
  const canAct = !forceReadOnly && showCompleteCta;
  const canJump = !forceReadOnly;

  return (
    <section
      className="flex flex-wrap items-center gap-4 rounded-[18px] border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-5 py-4 [background-image:linear-gradient(120deg,hsl(var(--ds-surface))_55%,hsl(var(--ds-primary)/0.1))]"
      aria-label="Profile overview"
    >
      <div className="relative h-[76px] w-[76px] shrink-0">
        <svg width="76" height="76" className="-rotate-90" aria-hidden>
          <circle
            cx="38"
            cy="38"
            r={r}
            fill="none"
            stroke="hsl(var(--ds-border))"
            strokeWidth="5"
          />
          <circle
            cx="38"
            cy="38"
            r={r}
            fill="none"
            stroke="hsl(var(--ds-primary))"
            strokeWidth="5"
            strokeLinecap="round"
            strokeDasharray={`${dash} ${c}`}
          />
        </svg>
        <div className="absolute inset-2 grid place-items-center rounded-full bg-[hsl(var(--ds-primary))] text-lg font-bold text-[hsl(var(--ds-primary-fg))]">
          {initialsFromName(name)}
        </div>
        <span className="absolute -bottom-1 -right-1 rounded-full bg-[hsl(var(--ds-fg))] px-1.5 text-[11px] font-medium text-[hsl(var(--ds-surface))]">
          {pct}%
        </span>
      </div>

      <div className="min-w-[220px] flex-1">
        <h1 className="text-2xl font-bold leading-8 text-[hsl(var(--ds-fg))]">{name}</h1>
        {sub ? (
          <p className="mt-0.5 text-sm text-[hsl(var(--ds-muted-fg))]">{sub}</p>
        ) : null}
        <div className="mt-2 flex flex-wrap gap-1.5">
          {empCode ? <DsBadge tone="primary">{empCode}</DsBadge> : null}
          {status ? <DsBadge tone="warning">{status}</DsBadge> : null}
          {type ? <DsBadge tone="neutral">{type}</DsBadge> : null}
        </div>
      </div>

      {incompleteSections.length > 0 ? (
        <div className="flex max-w-[380px] flex-wrap items-center gap-1.5">
          <span className="text-sm text-[hsl(var(--ds-muted-fg))]">
            {incompleteSections.length} to complete
          </span>
          {missing.map((item) => (
            <button
              key={item.id}
              type="button"
              disabled={!canJump || item.locked}
              onClick={() => onJumpToSection?.(item.id)}
              className="rounded-full border border-[hsl(var(--ds-warning)/0.55)] bg-[hsl(var(--ds-warning)/0.12)] px-2.5 py-0.5 text-xs font-medium text-[hsl(var(--ds-fg))] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {item.label}
            </button>
          ))}
          {extra > 0 ? (
            <button
              type="button"
              disabled={!canJump || incompleteSections[4]?.locked}
              onClick={() => onJumpToSection?.(incompleteSections[4]?.id)}
              className="rounded-full border border-[hsl(var(--ds-warning)/0.55)] bg-[hsl(var(--ds-warning)/0.12)] px-2.5 py-0.5 text-xs font-medium text-[hsl(var(--ds-fg))] disabled:cursor-not-allowed disabled:opacity-50"
            >
              +{extra}
            </button>
          ) : null}
        </div>
      ) : (
        <DsBadge tone="success">Profile complete</DsBadge>
      )}

      {canAct ? (
        <DsButton
          variant="primary"
          size="md"
          disabled={incompleteSections.length === 0}
          onClick={onCompleteProfile}
          className="shrink-0"
        >
          Complete my profile
        </DsButton>
      ) : null}
    </section>
  );
}
